"use client";

import * as React from "react";
import { motion } from "motion/react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface SectionHeaderProps {
  icon: LucideIcon;
  overline: string;
  overlineClassName?: string;
  pillClassName?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  align?: "left" | "center";
  badge?: string;
}

export function SectionHeader({
  icon: Icon,
  overline,
  overlineClassName,
  pillClassName,
  title,
  subtitle,
  align = "center",
  badge,
}: SectionHeaderProps) {
  return (
    <div className={cn("mb-12 lg:mb-16", align === "center" && "text-center")}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className={cn(
          "inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-5",
          pillClassName,
        )}
      >
        <Icon className={cn("size-3.5", overlineClassName)} />
        <span className={cn("text-overline", overlineClassName)}>{overline}</span>
        {badge && (
          <>
            <span className="text-muted-foreground opacity-30">·</span>
            <span className="text-overline text-muted-foreground">{badge}</span>
          </>
        )}
      </motion.div>
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.05 }}
        className="text-display-lg mb-4 max-w-4xl"
        style={align === "center" ? { marginLeft: "auto", marginRight: "auto" } : undefined}
      >
        {title}
      </motion.h2>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-body-lg text-muted-foreground max-w-3xl leading-relaxed"
          style={align === "center" ? { marginLeft: "auto", marginRight: "auto" } : undefined}
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}
